import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { ClipboardList, ExternalLink, HelpCircle, LogOut, Sun, Tag } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { LogoMark } from "@/components/site/Logo";

const NAV = [
  { to: "/admin", label: "Leads inbox", icon: ClipboardList, end: true },
  { to: "/admin/products", label: "Products", icon: Sun },
  { to: "/admin/rebates", label: "Rebates", icon: Tag },
  { to: "/admin/faqs", label: "FAQs", icon: HelpCircle },
];

const TITLES = {
  "/admin": "Leads inbox",
  "/admin/products": "Product manager",
  "/admin/rebates": "Rebates manager",
  "/admin/faqs": "FAQ manager",
};

export default function AdminShell() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const doLogout = async () => {
    await logout();
    navigate("/admin/login", { replace: true });
  };

  const title = TITLES[location.pathname] || "Admin";

  return (
    <div className="flex min-h-screen bg-[#F3F4F1]" data-testid="admin-shell">
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-60 flex-col border-r border-forest-700/10 bg-white lg:flex">
        <div className="flex items-center gap-2.5 border-b border-forest-700/10 px-5 py-4">
          <LogoMark className="h-8 w-8" />
          <div className="leading-none">
            <p className="font-display text-sm font-extrabold text-forest-700">Melwest Admin</p>
            <p className="mt-1 text-[10px] font-bold uppercase tracking-[0.18em] text-moss">{user?.email}</p>
          </div>
        </div>
        <nav className="flex-1 space-y-1 px-3 py-4">
          {NAV.map(({ to, label, icon: Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              data-testid={`admin-nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-semibold transition-colors ${
                  isActive ? "bg-forest-700 text-bone" : "text-moss hover:bg-sage/60 hover:text-forest-700"
                }`
              }
            >
              <Icon className="h-4 w-4" /> {label}
            </NavLink>
          ))}
        </nav>
        <div className="space-y-1 border-t border-forest-700/10 px-3 py-4">
          <Link to="/" data-testid="admin-view-site" className="flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-semibold text-moss hover:bg-sage/60 hover:text-forest-700">
            <ExternalLink className="h-4 w-4" /> View site
          </Link>
          <button onClick={doLogout} data-testid="admin-logout" className="flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-sm font-semibold text-red-600 hover:bg-red-50">
            <LogOut className="h-4 w-4" /> Log out
          </button>
        </div>
      </aside>

      <div className="flex-1 lg:pl-60">
        <header className="sticky top-0 z-30 border-b border-forest-700/10 bg-white/90 backdrop-blur">
          <div className="flex items-center justify-between px-5 py-3.5">
            <h1 className="font-display text-lg font-extrabold text-forest-700" data-testid="admin-page-title">{title}</h1>
            <div className="flex items-center gap-3">
              <div className="hidden gap-1 lg:hidden xl:flex" />
              <button onClick={doLogout} data-testid="admin-logout-mobile" className="rounded-md border border-forest-700/15 px-3 py-1.5 text-xs font-bold text-red-600 lg:hidden">
                Log out
              </button>
            </div>
          </div>
          <nav className="flex gap-1 overflow-x-auto px-3 pb-2 lg:hidden">
            {NAV.map(({ to, label, end }) => (
              <NavLink
                key={to}
                to={to}
                end={end}
                className={({ isActive }) =>
                  `whitespace-nowrap rounded-full px-3.5 py-1.5 text-xs font-bold ${
                    isActive ? "bg-forest-700 text-bone" : "border border-forest-700/15 text-moss"
                  }`
                }
              >
                {label}
              </NavLink>
            ))}
          </nav>
        </header>
        <main className="px-5 py-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
